import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { BarChart3, TrendingUp, Bot, AlertTriangle, Filter } from 'lucide-react';

function MiniBarChart({ data, maxHeight = 60, barColor = 'bg-primary' }) {
  if (!data || data.length === 0) return <p className="text-xs text-muted-foreground text-center py-4">No data yet</p>;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-[2px]" style={{ height: maxHeight }}>
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center justify-end" title={`${d.date || d.stage || d.agent || d.template || d.error}: ${d.count}`}>
          <div className={`w-full ${barColor} rounded-t-sm transition-all`} style={{ height: `${(d.count / max) * maxHeight}px`, minHeight: d.count > 0 ? 2 : 0 }} />
        </div>
      ))}
    </div>
  );
}

function FunnelChart({ data }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="space-y-2">
      {data.map((d, i) => (
        <div key={i}>
          <div className="flex justify-between text-[11px] sm:text-xs mb-0.5">
            <span className="truncate mr-2">{d.stage}</span>
            <span className="font-mono font-medium flex-shrink-0">{d.count}</span>
          </div>
          <div className="h-4 sm:h-5 bg-muted/30 rounded overflow-hidden">
            <div className="h-full bg-primary/60 rounded transition-all" style={{ width: `${(d.count / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function HBarChart({ items = [], colorClass = 'bg-cyan-500/50' }) {
  if (!items.length) return <p className="text-xs text-muted-foreground text-center py-4">No data yet</p>;
  const max = items[0]?.count || 1;
  return (
    <div className="space-y-1.5">
      {items.map((a, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-[10px] sm:text-xs w-20 sm:w-28 truncate">{a.agent || a.template}</span>
          <div className="flex-1 h-3 sm:h-4 bg-muted/30 rounded overflow-hidden"><div className={`h-full ${colorClass} rounded`} style={{ width: `${(a.count / max) * 100}%` }} /></div>
          <span className="text-[10px] sm:text-xs font-mono w-6 sm:w-8 text-right">{a.count}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminAnalyticsPage() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [agents, setAgents] = useState(null);
  const [errors, setErrors] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, [days]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, a, e, f] = await Promise.all([
        adminAPI.activitySummary(days),
        adminAPI.analyticsAgents(days),
        adminAPI.analyticsErrors(days),
        adminAPI.analyticsFunnel(),
      ]);
      setSummary(s.data); setAgents(a.data); setErrors(e.data); setFunnel(f.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  if (loading) return <div className="p-4 sm:p-6 space-y-4"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-2 gap-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40" />)}</div></div>;

  return (
    <div className="p-4 sm:p-6 max-w-[1400px]" data-testid="admin-analytics">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Analytics</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Platform usage, engagement, and health</p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-5">
        <Card><CardContent className="p-3"><p className="text-[10px] text-muted-foreground">Active Users</p><p className="text-xl sm:text-2xl font-bold font-mono">{summary?.active_users || 0}</p><p className="text-[9px] text-muted-foreground">Today: {summary?.today_active || 0}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-[10px] text-muted-foreground">Total Events</p><p className="text-xl sm:text-2xl font-bold font-mono">{summary?.total_events || 0}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-[10px] text-muted-foreground">Errors</p><p className="text-xl sm:text-2xl font-bold font-mono text-red-400">{summary?.error_count || 0}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-[10px] text-muted-foreground">Events by Type</p><div className="mt-1 space-y-0.5">{Object.entries(summary?.events_by_type || {}).slice(0, 4).map(([k, v]) => <div key={k} className="flex justify-between text-[9px] sm:text-[10px]"><span className="text-muted-foreground">{k}</span><span className="font-mono">{v}</span></div>)}</div></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-5">
        <Card>
          <CardHeader className="py-2.5 px-3 sm:px-4"><CardTitle className="text-xs sm:text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Daily Activity</CardTitle></CardHeader>
          <CardContent className="px-3 sm:px-4 pb-3">
            <MiniBarChart data={summary?.events_by_day || []} maxHeight={70} />
            <div className="flex justify-between text-[9px] text-muted-foreground mt-1">
              <span>{summary?.events_by_day?.[0]?.date || ''}</span>
              <span>{summary?.events_by_day?.[summary.events_by_day.length - 1]?.date || ''}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="py-2.5 px-3 sm:px-4"><CardTitle className="text-xs sm:text-sm flex items-center gap-2"><Filter className="w-4 h-4" /> Conversion Funnel</CardTitle></CardHeader>
          <CardContent className="px-3 sm:px-4 pb-3">
            <FunnelChart data={funnel?.funnel || []} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="py-2.5 px-3 sm:px-4"><CardTitle className="text-xs sm:text-sm flex items-center gap-2"><Bot className="w-4 h-4" /> Agent Usage</CardTitle></CardHeader>
          <CardContent className="px-3 sm:px-4 pb-3">
            <HBarChart items={agents?.agents || []} colorClass="bg-cyan-500/50" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="py-2.5 px-3 sm:px-4"><CardTitle className="text-xs sm:text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Template Usage</CardTitle></CardHeader>
          <CardContent className="px-3 sm:px-4 pb-3">
            <HBarChart items={agents?.templates || []} colorClass="bg-emerald-500/50" />
          </CardContent>
        </Card>
      </div>

      {(errors?.errors || []).length > 0 && (
        <Card>
          <CardHeader className="py-2.5 px-3 sm:px-4"><CardTitle className="text-xs sm:text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" /> Recent Errors</CardTitle></CardHeader>
          <CardContent className="px-3 sm:px-4 pb-3">
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {errors.errors.slice(0, 20).map((e, i) => (
                <div key={i} className="text-[11px] border-b border-border/30 pb-2">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5">
                    <span className="font-medium text-red-400">{e.event_name}</span>
                    <span className="text-muted-foreground text-[10px]">{new Date(e.timestamp).toLocaleString()}</span>
                  </div>
                  <p className="text-muted-foreground mt-0.5 break-all">{e.metadata?.detail || e.metadata?.endpoint || 'No details'}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
